import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from "react";
import nlTranslations from "@/i18n/nl";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type Language = "nl" | "en";

// NL is always bundled (primary language). EN is loaded on demand.
const translationCache: Record<Language, Record<string, string>> = {
  nl: nlTranslations,
  en: {},
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function detectLangFromPath(): Language | null {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname;
  if (path.startsWith("/en/") || path === "/en") return "en";
  return null;
}

function getInitialLanguage(): Language {
  const urlLang = detectLangFromPath();
  if (urlLang) return urlLang;
  if (typeof window === "undefined") return "nl";

  const stored = localStorage.getItem("language");
  if (stored === "nl" || stored === "en") return stored;

  const browserLang = navigator.language.substring(0, 2);
  return browserLang === "en" ? "en" : "nl";
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => getInitialLanguage());

  const [enLoaded, setEnLoaded] = useState(false);

  // Lazy-load EN translations only when needed
  useEffect(() => {
    if (language === "en" && !enLoaded) {
      import("@/i18n/en").then((mod) => {
        translationCache.en = mod.default;
        setEnLoaded(true);
      });
    }
  }, [language, enLoaded]);

  // Fetch custom overrides from database
  const { data: dbOverrides } = useQuery({
    queryKey: ["translations-overrides"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("translations" as any)
        .select("key, nl, en");
      if (error) {
        return {};
      }
      const overrides: Record<Language, Record<string, string>> = { nl: {}, en: {} };
      for (const row of (data || []) as any[]) {
        if (row.nl) overrides.nl[row.key] = row.nl;
        if (row.en) overrides.en[row.key] = row.en;
      }
      return overrides;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    const urlLang = detectLangFromPath();
    if (urlLang && urlLang !== language) {
      setLanguageState(urlLang);
      localStorage.setItem("language", urlLang);
    } else if (!urlLang && language === "en") {
      const path = window.location.pathname;
      if (!path.startsWith("/dashboard") && !path.startsWith("/admin")) {
        setLanguageState("nl");
        localStorage.setItem("language", "nl");
      }
    }
  }, [typeof window !== "undefined" ? window.location.pathname : ""]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
    document.documentElement.lang = lang;
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback(
    (key: string, fallback?: string): string => {
      const dbValue = dbOverrides?.[language]?.[key];
      if (dbValue) return dbValue;

      const staticValue = translationCache[language][key];
      if (staticValue) return staticValue;

      if (language === "en") {
        const nlFallback = translationCache.nl[key];
        if (nlFallback) return nlFallback;
      }

      return fallback || key;
    },
    [language, dbOverrides, enLoaded]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (context) return context;

  const language = getInitialLanguage();

  if (typeof window !== "undefined") {
  }

  return {
    language,
    setLanguage: (lang: Language) => {
      if (typeof window === "undefined") return;
      localStorage.setItem("language", lang);
      document.documentElement.lang = lang;
    },
    t: (key: string, fallback?: string) => {
      const staticValue = translationCache[language][key];
      if (staticValue) return staticValue;

      if (language === "en") {
        const nlFallback = translationCache.nl[key];
        if (nlFallback) return nlFallback;
      }

      return fallback || key;
    },
  };
};
